import { match } from '@/utils/pattern-match-util'
import { type ReactNode } from 'react'
import cx from 'classnames'
import { AvatarProgress } from '@/components/AvatarProgress'
import {
  ClickableDateCell,
  ClickableImageCell,
  ClickableTableCell,
  ClickableTableRow,
  ClickableTextCell,
} from '@/components/Table/ClickableTable'
import { Checkbox } from '@/components/Checkbox'
import { PillCell } from '@/components/Table/PillCell'
import { type ColumnId } from '@/features/jobs/list/jobs-table-columns'
import { Tooltip } from '@/components/Tooltip'
import { type ColumnDensity } from '@/components/Table/TableCell'
import { dateFormatters } from '@/utils/date-util'
import { type TestIdProps } from '@/components/Table/TableCell'
import { Icon } from '@/components/Icon'
import { UnreadBadge } from '@/components/UnreadBadge'
import { type JobsTableStatus } from '@/features/jobs/list/jobs-table-status'
import styles from './JobsTableRow.module.css'
import { type Id } from '@/infra/id'

export interface OpenTicketInfo {
  statusMessage: string
  lastUpdated: Date
}

export interface PrimaryCellProps extends TestIdProps {
  avatar: ReactNode
  displayName: string
  secondaryInfo: string
  unreadCount: number
  openTicket: OpenTicketInfo | null
  density: ColumnDensity
}

export function PrimaryCell({
  avatar,
  displayName,
  secondaryInfo,
  unreadCount,
  openTicket,
  density,
  testId,
}: PrimaryCellProps) {
  const showAvatar = density === 'Default'
  const showSecondaryInfo = density !== 'Compact'

  return (
    <ClickableTableCell>
      <div className={styles.primaryCellContainer}>
        <div className={styles.avatarContainer}>
          {unreadCount > 0 && (
            <UnreadBadge count={unreadCount} className={styles.unreadBadge} />
          )}
          {showAvatar && (
            <AvatarProgress size={36} progress={null}>
              {avatar}
            </AvatarProgress>
          )}
        </div>
        <div className={styles.primaryInfo}>
          <span className={styles.primaryText} data-test={testId}>
            {displayName}
            {openTicket && (
              <>
                <span className={styles.primaryTextSeparator}>&bull;</span>
                <OpenTicket openTicket={openTicket} />
              </>
            )}
          </span>
          {showSecondaryInfo && (
            <span className={styles.secondaryInfo}>{secondaryInfo}</span>
          )}
        </div>
      </div>
    </ClickableTableCell>
  )
}

interface OpenTicketProps {
  openTicket: OpenTicketInfo
}

function OpenTicket({ openTicket }: OpenTicketProps) {
  return (
    <Tooltip
      placement="top-start"
      content={
        <>
          {openTicket.statusMessage && <p>{openTicket.statusMessage}</p>}
          <p>
            Last updated{' '}
            {dateFormatters.dateTimeDefault.format(openTicket.lastUpdated)}
          </p>
        </>
      }
    >
      <span className={styles.openTicket}>
        Open ticket
        <span className={styles.openTicketIcon}>
          <Icon auto name="alert-blue" />
        </span>
      </span>
    </Tooltip>
  )
}

export interface StatusCellProps extends TestIdProps {
  status: JobsTableStatus
}

export function StatusCell({ status, testId }: StatusCellProps) {
  return (
    <ClickableTableCell
      className={cx(
        styles.statusCell,
        match(status.variant, {
          onboarding: styles.statusOnboarding,
          infoGathering: styles.statusInfoGathering,
          unclaimed: styles.statusUnclaimed,
          claimed: styles.statusClaimed,
          closed: styles.statusClosed,
          idle: styles.statusIdle,
          onHold: styles.statusOnHold,
          pending: styles.statusPending,
        }),
      )}
    >
      <span data-test={testId}>
        {status.text}
        {status.subtext && (
          <span className={styles.statusSubtext}>{` (${status.subtext})`}</span>
        )}
      </span>
    </ClickableTableCell>
  )
}

export interface TotalCellProps extends TestIdProps {
  total: string
}

export function TotalCell({ total, testId }: TotalCellProps) {
  return <ClickableTextCell testId={testId}>{total}</ClickableTextCell>
}

export interface JobsTableRowProps extends TestIdProps {
  /**
   * Where the row links to, or nothing when the job can't be opened.
   */
  to?: string
  children: ReactNode
}

export function JobsTableRow({ to, children, testId }: JobsTableRowProps) {
  return (
    <ClickableTableRow to={to} data-test={testId}>
      {children}
    </ClickableTableRow>
  )
}

/**
 * A person or workspace shown as an avatar and a title.
 */
export interface JobsTableImageValue {
  title?: string | null
  image?: string | null
  imageFallback?: string | null
}

/**
 * What the cells of a job's row show.
 */
export interface JobsTableCellValues {
  id: Id
  primary: Omit<PrimaryCellProps, 'density' | 'testId'>
  shortId: string
  legendName: string
  workspace: JobsTableImageValue
  status: JobsTableStatus
  payout: string
  total: string
  progressionStep: string
  scopeChangeStatus?: string
  clientTeamName?: string
  clientChampion: JobsTableImageValue
  clientChampionEmail?: string | null
  proChampion: JobsTableImageValue
  proChampionEmail?: string | null
  isRequestedPro: string
  dateCreated: Date
  dateSubmitted: Date | null
  dateClaimed: Date | null
  isCompleted: boolean
  dateCompleted: Date | null
  dateAdvanceAllowed: Date | null
  dateLastProMessageActivity: Date | null
  dateLastClientMessageActivity: Date | null
  dateLastRelationshipManagerMessageActivity: Date | null
}

export interface JobsTableCellsProps {
  columnIds: readonly ColumnId[]
  values: JobsTableCellValues
  density: ColumnDensity
  isSelected: boolean
  onToggleSelected: () => unknown
}

export function JobsTableCells({
  columnIds,
  values,
  density,
  isSelected,
  onToggleSelected,
}: JobsTableCellsProps) {
  const id = values.id
  return columnIds.map((columnId) => {
    switch (columnId) {
      case 'checkbox':
        return (
          <ClickableTableCell key={columnId}>
            <div>
              <Checkbox
                muteClickEvent
                checked={isSelected}
                onChange={onToggleSelected}
              />
            </div>
          </ClickableTableCell>
        )
      case 'jobTitle':
        return (
          <PrimaryCell
            {...values.primary}
            density={density}
            testId={`job-title-${id}`}
            key={columnId}
          />
        )
      case 'jobShortId':
        return (
          <ClickableTextCell testId={`job-short-id-${id}`} key={columnId}>
            {values.shortId}
          </ClickableTextCell>
        )
      case 'legendName':
        return (
          <ClickableTextCell testId={`job-legend-name-${id}`} key={columnId}>
            {values.legendName}
          </ClickableTextCell>
        )
      case 'workspace':
        return (
          <ClickableImageCell
            {...values.workspace}
            testId={`job-workspace-${id}`}
            key={columnId}
          />
        )
      case 'status':
        return (
          <StatusCell
            status={values.status}
            testId={`job-status-${id}`}
            key={columnId}
          />
        )
      case 'payout':
        return (
          <ClickableTextCell testId={`job-payout-${id}`} key={columnId}>
            {values.payout}
          </ClickableTextCell>
        )
      case 'total':
        return (
          <TotalCell
            total={values.total}
            testId={`job-total-${id}`}
            key={columnId}
          />
        )
      case 'progressionStep':
        return (
          <ClickableTextCell
            testId={`job-progression-step-${id}`}
            key={columnId}
          >
            {values.progressionStep}
          </ClickableTextCell>
        )
      case 'jobScopeChangeRequestStatus':
        return (
          <PillCell
            testId={`job-scope-change-request-status-${id}`}
            key={columnId}
          >
            {values.scopeChangeStatus}
          </PillCell>
        )
      case 'clientTeam':
        return (
          <ClickableTextCell testId={`job-client-team-${id}`} key={columnId}>
            {values.clientTeamName}
          </ClickableTextCell>
        )
      case 'clientChampion':
        return (
          <ClickableImageCell
            {...values.clientChampion}
            testId={`job-client-champion-${id}`}
            key={columnId}
          />
        )
      case 'clientChampionEmail':
        return (
          <ClickableTextCell
            testId={`job-client-champion-email-${id}`}
            key={columnId}
          >
            {values.clientChampionEmail}
          </ClickableTextCell>
        )
      case 'proChampion':
        return (
          <ClickableImageCell
            {...values.proChampion}
            testId={`job-pro-champion-${id}`}
            key={columnId}
          />
        )
      case 'proChampionEmail':
        return (
          <ClickableTextCell
            testId={`job-pro-champion-email-${id}`}
            key={columnId}
          >
            {values.proChampionEmail}
          </ClickableTextCell>
        )
      case 'isRequestedPro':
        return (
          <ClickableTextCell
            testId={`job-is-requested-pro-${id}`}
            key={columnId}
          >
            {values.isRequestedPro}
          </ClickableTextCell>
        )
      case 'created':
        return (
          <ClickableDateCell
            date={values.dateCreated}
            density={density}
            testId={`job-created-date-${id}`}
            key={columnId}
          />
        )
      case 'submitted':
        return (
          <ClickableDateCell
            date={values.dateSubmitted}
            density={density}
            testId={`job-submitted-date-${id}`}
            key={columnId}
          />
        )
      case 'claimed':
        return (
          <ClickableDateCell
            date={values.dateClaimed}
            density={density}
            testId={`job-claimed-date-${id}`}
            key={columnId}
          />
        )
      case 'completed':
        return values.isCompleted ? (
          <ClickableDateCell
            date={values.dateCompleted}
            density={density}
            testId={`job-completed-date-${id}`}
            key={columnId}
          />
        ) : (
          <ClickableTableCell key={columnId} />
        )
      case 'advanceAllowed':
        return (
          <ClickableDateCell
            date={values.dateAdvanceAllowed}
            density={density}
            testId={`job-advance-allowed-date-${id}`}
            key={columnId}
          />
        )
      case 'lastProMessageActivity':
        return (
          <ClickableDateCell
            date={values.dateLastProMessageActivity}
            density={density}
            testId={`job-last-pro-message-activity-date-${id}`}
            key={columnId}
          />
        )
      case 'lastClientMessageActivity':
        return (
          <ClickableDateCell
            date={values.dateLastClientMessageActivity}
            density={density}
            testId={`job-last-client-message-activity-date-${id}`}
            key={columnId}
          />
        )
      case 'lastRelationshipManagerMessageActivity':
        return (
          <ClickableDateCell
            date={values.dateLastRelationshipManagerMessageActivity}
            density={density}
            testId={`job-last-relationship-manager-message-activity-date-${id}`}
            key={columnId}
          />
        )
      default:
        return <ClickableTableCell key={columnId} />
    }
  })
}
